import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  Building2, Users, Palette, Clock, Eye, CheckCircle, XCircle, 
  AlertTriangle, Search, RefreshCw, Loader2, Phone, Mail,
  ExternalLink, Sparkles, Filter, Calendar, TrendingUp,
  BadgeCheck, MoreHorizontal, ChevronRight
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';

export default function AdminRecentActivity() {
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entityType: 'all',
    period: 'all',
    status: 'all'
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  async function loadActivity() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        entity_type: filters.entityType,
        period: filters.period,
        page: page.toString(),
        limit: '50'
      });
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      
      const res = await authAxios.get(`${API}/admin/recent-activity?${params}`, { withCredentials: true });
      setActivities(res.data.activities || []);
      setSummary(res.data.summary || {});
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar actividad');
    }
    setLoading(false);
  }

  useEffect(() => {
    loadActivity();
  }, [filters, page]);

  async function markAsReviewed(entityType, entityId) {
    try {
      await authAxios.post(`${API}/admin/mark-reviewed/${entityType}/${entityId}`, {}, { withCredentials: true });
      toast.success('Marcado como revisado');
      loadActivity();
    } catch (err) {
      toast.error('Error al marcar como revisado');
    }
  }

  function getEntityIcon(type) {
    switch (type) {
      case 'center': return <Building2 className="w-4 h-4" />;
      case 'professional': return <Users className="w-4 h-4" />;
      case 'workshop': return <Palette className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  }

  function getEntityColor(type) {
    switch (type) {
      case 'center': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'professional': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'workshop': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  }

  function getEntityLabel(type) {
    switch (type) {
      case 'center': return 'Centro';
      case 'professional': return 'Profesional';
      case 'workshop': return 'Taller';
      default: return type;
    }
  }

  function getEntityLink(activity) {
    switch (activity.entity_type) {
      case 'center': return `/centro/${activity.entity_id}`;
      case 'professional': return `/profesional/${activity.entity_id}`;
      case 'workshop': return `/taller/${activity.entity_id}`;
      default: return '#';
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      
      if (diffHours < 1) return 'Hace minutos';
      if (diffHours < 24) return `Hace ${diffHours}h`;
      if (diffDays === 1) return 'Ayer';
      if (diffDays < 7) return `Hace ${diffDays} días`;
      return date.toLocaleDateString('es-CL');
    } catch {
      return 'N/A';
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-50">{summary.total || 0}</p>
                  <p className="text-xs text-slate-400">Total actividad</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{summary.new_count || 0}</p>
                  <p className="text-xs text-slate-400">Nuevos (48h)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{summary.unverified_count || 0}</p>
                  <p className="text-xs text-slate-400">Sin verificar</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">{summary.incomplete_count || 0}</p>
                  <p className="text-xs text-slate-400">Incompletos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Type distribution */}
      {summary?.by_type && (
        <div className="flex gap-4 flex-wrap">
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-3 py-1">
            <Building2 className="w-3 h-3 mr-1" /> {summary.by_type.centers || 0} Centros
          </Badge>
          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 px-3 py-1">
            <Users className="w-3 h-3 mr-1" /> {summary.by_type.professionals || 0} Profesionales
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 px-3 py-1">
            <Palette className="w-3 h-3 mr-1" /> {summary.by_type.workshops || 0} Talleres
          </Badge>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filtros:</span>
            </div>
            
            <Select value={filters.entityType} onValueChange={(v) => setFilters({...filters, entityType: v})}>
              <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-slate-200">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-slate-200">Todos</SelectItem>
                <SelectItem value="centers" className="text-slate-200">Centros</SelectItem>
                <SelectItem value="professionals" className="text-slate-200">Profesionales</SelectItem>
                <SelectItem value="workshops" className="text-slate-200">Talleres</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.period} onValueChange={(v) => setFilters({...filters, period: v})}>
              <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-slate-200">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-slate-200">Todos</SelectItem>
                <SelectItem value="today" className="text-slate-200">Hoy</SelectItem>
                <SelectItem value="week" className="text-slate-200">Última semana</SelectItem>
                <SelectItem value="month" className="text-slate-200">Último mes</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
              <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700 text-slate-200">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-slate-200">Todos</SelectItem>
                <SelectItem value="new" className="text-slate-200">Nuevos registros</SelectItem>
                <SelectItem value="edited" className="text-slate-200">Editados</SelectItem>
                <SelectItem value="unverified" className="text-slate-200">Sin verificar</SelectItem>
                <SelectItem value="incomplete" className="text-slate-200">Incompletos</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadActivity}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-400" />
            Actividad Reciente
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No hay actividad para mostrar con estos filtros
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {activities.map((activity, idx) => (
                <div key={`${activity.entity_type}-${activity.entity_id}-${idx}`} className="p-4 hover:bg-slate-800/50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      activity.entity_type === 'center' ? 'bg-emerald-500/20' :
                      activity.entity_type === 'professional' ? 'bg-cyan-500/20' : 'bg-purple-500/20'
                    }`}>
                      {getEntityIcon(activity.entity_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-medium text-slate-50 truncate">{activity.name}</span>
                        
                        {/* Badges */}
                        <Badge className={`text-xs ${getEntityColor(activity.entity_type)}`}>
                          {getEntityLabel(activity.entity_type)}
                        </Badge>
                        
                        {activity.is_new && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                            NUEVO
                          </Badge>
                        )}
                        
                        {activity.is_verified ? (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                            <BadgeCheck className="w-3 h-3 mr-1" /> Verificado
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                            Sin verificar
                          </Badge>
                        )}
                        
                        {activity.is_premium && (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                            <Sparkles className="w-3 h-3 mr-1" /> Premium
                          </Badge>
                        )}
                      </div>
                      
                      {/* Details */}
                      <div className="flex items-center gap-4 text-sm text-slate-400 flex-wrap">
                        {activity.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {activity.email}
                          </span>
                        )}
                        {activity.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {activity.phone}
                          </span>
                        )}
                        {activity.city && (
                          <span>{activity.city}</span>
                        )}
                      </div>
                      
                      {/* Profile completion */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 max-w-[150px] h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              activity.profile_completion >= 80 ? 'bg-green-500' :
                              activity.profile_completion >= 50 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${activity.profile_completion}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">{activity.profile_completion}% completo</span>
                      </div>
                    </div>
                    
                    {/* Time and Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-slate-500">{formatDate(activity.created_at)}</span>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-200">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-slate-800 border-slate-700" align="end">
                          <DropdownMenuItem asChild className="text-slate-200 focus:bg-slate-700 cursor-pointer">
                            <Link to={getEntityLink(activity)} target="_blank">
                              <Eye className="w-4 h-4 mr-2" /> Ver perfil público
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-slate-200 focus:bg-slate-700 cursor-pointer"
                            onClick={() => markAsReviewed(activity.entity_type, activity.entity_id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" /> Marcar como revisado
                          </DropdownMenuItem>
                          {activity.email && (
                            <DropdownMenuItem asChild className="text-slate-200 focus:bg-slate-700 cursor-pointer">
                              <a href={`mailto:${activity.email}`}>
                                <Mail className="w-4 h-4 mr-2" /> Enviar email
                              </a>
                            </DropdownMenuItem>
                          )}
                          {activity.phone && (
                            <DropdownMenuItem asChild className="text-slate-200 focus:bg-slate-700 cursor-pointer">
                              <a href={`https://wa.me/${activity.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                                <Phone className="w-4 h-4 mr-2" /> WhatsApp
                              </a>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t border-slate-800">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="border-slate-700 text-slate-300"
              >
                Anterior
              </Button>
              <span className="flex items-center px-3 text-sm text-slate-400">
                Página {page} de {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="border-slate-700 text-slate-300"
              >
                Siguiente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
